import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Reveal from "./Reveal";
import { ArrowRight, Loader2, Phone, Mail, ShieldCheck } from "lucide-react";

const initial = {
  full_name: "", email: "", phone: "", origin: "", destination: "",
  vehicle_year: "", vehicle_make: "", vehicle_model: "",
  transport_type: "open", ship_date: "", notes: "",
};

const inputCls =
  "w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-ksy-ink placeholder-slate-400 focus:border-ksy-blue focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors";
const labelCls = "block text-sm font-semibold text-slate-700 mb-1.5";

export default function QuoteForm() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", form);
      toast.success(t("quote.success"));
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("quote.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="py-20 md:py-28 bg-ksy-navy" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        <Reveal className="lg:col-span-2 lg:pt-6">
          <span className="text-sm font-bold uppercase tracking-[0.18em] text-blue-300">{t("quote.label")}</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">{t("quote.title")}</h2>
          <p className="mt-4 text-lg text-blue-100/70 leading-relaxed">{t("quote.subtitle")}</p>
          <div className="mt-8 space-y-4">
            <a href="tel:8069302872" className="flex items-center gap-3 text-white hover:text-blue-200 transition-colors">
              <span className="grid place-items-center w-10 h-10 rounded-lg bg-white/10"><Phone className="w-5 h-5" /></span>
              <span className="font-semibold">(806) 930-2872</span>
            </a>
            <a href="mailto:contact@ksytransport.com" className="flex items-center gap-3 text-white hover:text-blue-200 transition-colors">
              <span className="grid place-items-center w-10 h-10 rounded-lg bg-white/10"><Mail className="w-5 h-5" /></span>
              <span className="font-semibold">contact@ksytransport.com</span>
            </a>
            <div className="flex items-center gap-3 text-blue-100/70 pt-2">
              <ShieldCheck className="w-5 h-5 text-green-400" /> <span className="text-sm">No hidden fees · Free, no-obligation quote</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 grid sm:grid-cols-2 gap-4" data-testid="quote-form">
            <div><label className={labelCls}>{t("quote.name")}</label><input required value={form.full_name} onChange={set("full_name")} className={inputCls} data-testid="quote-name" /></div>
            <div><label className={labelCls}>{t("quote.email")}</label><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="quote-email" /></div>
            <div><label className={labelCls}>{t("quote.phone")}</label><input required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="quote-phone" /></div>
            <div><label className={labelCls}>{t("quote.type")}</label>
              <select value={form.transport_type} onChange={set("transport_type")} className={inputCls} data-testid="quote-type">
                <option value="open">{t("quote.open")}</option>
                <option value="enclosed">{t("quote.enclosed")}</option>
              </select>
            </div>
            <div><label className={labelCls}>{t("quote.origin")}</label><input required value={form.origin} onChange={set("origin")} className={inputCls} data-testid="quote-origin" /></div>
            <div><label className={labelCls}>{t("quote.destination")}</label><input required value={form.destination} onChange={set("destination")} className={inputCls} data-testid="quote-destination" /></div>
            <div><label className={labelCls}>{t("quote.year")}</label><input required value={form.vehicle_year} onChange={set("vehicle_year")} className={inputCls} data-testid="quote-year" /></div>
            <div><label className={labelCls}>{t("quote.make")}</label><input required value={form.vehicle_make} onChange={set("vehicle_make")} className={inputCls} data-testid="quote-make" /></div>
            <div><label className={labelCls}>{t("quote.model")}</label><input required value={form.vehicle_model} onChange={set("vehicle_model")} className={inputCls} data-testid="quote-model" /></div>
            <div><label className={labelCls}>{t("quote.date")}</label><input type="date" value={form.ship_date} onChange={set("ship_date")} className={inputCls} data-testid="quote-date" /></div>
            <div className="sm:col-span-2"><label className={labelCls}>{t("quote.notes")}</label><textarea rows={3} value={form.notes} onChange={set("notes")} className={inputCls} data-testid="quote-notes" /></div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-ksy-blue text-white font-bold px-6 py-3.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60" data-testid="quote-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? t("quote.submitting") : t("quote.submit")}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
